import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ShieldCheck, DollarSign, TrendingUp, Users, AlertTriangle, Loader2, Mail,
    Lock, Radio, RefreshCw, Circle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const RANGE_OPTIONS = [
    { key: "today", label: "Today" },
    { key: "7d", label: "7d" },
    { key: "30d", label: "30d" },
    { key: "90d", label: "90d" },
    { key: "all", label: "All" },
];

const SESSION_KEY = "simonara.revenueToken.v1";

const Stat = ({ label, value, tone = "foreground", Icon, testId }) => (
    <div className="card-soft p-4" data-testid={testId}>
        <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            {Icon && <Icon size={11} />} {label}
        </div>
        <div className={`mt-1 font-heading font-bold text-2xl mono-score text-${tone}`}>{value}</div>
    </div>
);

const money = (n) => {
    const v = Number(n || 0);
    return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
};

const relTime = (iso) => {
    if (!iso) return "—";
    const dt = new Date(iso);
    const s = Math.floor((Date.now() - dt.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

// ---------- 2FA gate ----------
function TwoFactorGate({ onUnlock }) {
    const [phase, setPhase] = useState("idle"); // idle | sending | sent | verifying
    const [maskedEmail, setMaskedEmail] = useState("");
    const [code, setCode] = useState("");

    const requestCode = async () => {
        try {
            setPhase("sending");
            const { data } = await api.post("/admin/revenue/2fa/request");
            setMaskedEmail(data.sent_to_masked || "your admin inbox");
            setPhase("sent");
            toast.success("Code sent — check your admin inbox.");
        } catch (err) {
            setPhase("idle");
            toast.error(formatApiError(err));
        }
    };
    const verify = async () => {
        if (!/^\d{6}$/.test(code.trim())) {
            toast.error("Enter the 6-digit code.");
            return;
        }
        try {
            setPhase("verifying");
            const { data } = await api.post("/admin/revenue/2fa/verify", { code: code.trim() });
            sessionStorage.setItem(SESSION_KEY, data.revenue_token);
            onUnlock(data.revenue_token);
        } catch (err) {
            setPhase("sent");
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="mx-auto max-w-md" data-testid="revenue-2fa-gate">
            <div className="card-soft p-8 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                    <Lock size={22} className="text-emerald-500" />
                </div>
                <h2 className="font-heading font-medium text-2xl">Revenue view locked</h2>
                <p className="text-sm text-muted-foreground mt-2">
                    Live payment data is highly sensitive. Verify with a one-time code sent to your admin inbox to unlock this page for 30 minutes.
                </p>
                {phase === "idle" && (
                    <Button data-testid="req-revenue-code" onClick={requestCode} className="mt-6 w-full">
                        <Mail size={14} className="mr-2" /> Send me a code
                    </Button>
                )}
                {phase === "sending" && (
                    <Button disabled className="mt-6 w-full">
                        <Loader2 size={14} className="animate-spin mr-2" /> Sending...
                    </Button>
                )}
                {(phase === "sent" || phase === "verifying") && (
                    <div className="mt-6 space-y-3 text-left">
                        <div className="text-xs text-muted-foreground">
                            Code sent to <span className="font-mono text-foreground">{maskedEmail}</span>. Expires in 10 min.
                        </div>
                        <Input
                            data-testid="revenue-code-input"
                            autoFocus
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="000000"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                            className="text-center text-2xl tracking-[0.5em] font-mono"
                        />
                        <Button
                            data-testid="verify-revenue-code"
                            onClick={verify}
                            disabled={phase === "verifying" || code.length !== 6}
                            className="w-full"
                        >
                            {phase === "verifying" ? <Loader2 size={14} className="animate-spin mr-2" /> : <ShieldCheck size={14} className="mr-2" />}
                            Unlock revenue view
                        </Button>
                        <button
                            type="button"
                            onClick={requestCode}
                            className="text-xs text-muted-foreground hover:text-foreground w-full text-center"
                        >
                            Didn&apos;t get it? Resend
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------- Dashboard body ----------
function RevenueDashboard({ token, onLock }) {
    const [range, setRange] = useState("30d");
    const [stats, setStats] = useState(null);
    const [feed, setFeed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nowTs, setNowTs] = useState(Date.now());

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [{ data: s }, { data: f }] = await Promise.all([
                api.get(`/admin/revenue/stats?range=${range}`, { headers: { "X-Revenue-Token": token } }),
                api.get("/admin/revenue/feed?limit=100", { headers: { "X-Revenue-Token": token } }),
            ]);
            setStats(s);
            setFeed(f.items || []);
        } catch (err) {
            if (err?.response?.status === 401) {
                sessionStorage.removeItem(SESSION_KEY);
                onLock();
                return;
            }
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [range, token, onLock]);

    useEffect(() => { load(); }, [load]);
    // Auto-refresh feed every 30s
    useEffect(() => {
        const t = setInterval(() => { load(); setNowTs(Date.now()); }, 30_000);
        return () => clearInterval(t);
    }, [load]);
    // Tick the "Xs ago" labels every 10s
    useEffect(() => {
        const t = setInterval(() => setNowTs(Date.now()), 10_000);
        return () => clearInterval(t);
    }, []);
    void nowTs; // trigger re-render only

    const topPlan = useMemo(() => (stats?.by_plan || [])[0], [stats]);

    return (
        <div data-testid="admin-revenue-page">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="overline">Command Center · Revenue Ops</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground flex items-center gap-2">
                        Live Revenue
                        <Badge variant="outline" className="text-emerald-600 border-emerald-500/40">
                            <Radio size={10} className="mr-1 animate-pulse" /> Live
                        </Badge>
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Real-time payments across every plan. Auto-refresh every 30s. Session unlock expires in 30 min.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="revenue-range">
                        {RANGE_OPTIONS.map((r) => (
                            <button
                                key={r.key}
                                onClick={() => setRange(r.key)}
                                className={`px-3 py-1.5 text-xs font-medium ${range === r.key ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                                data-testid={`range-${r.key}`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <Button variant="outline" size="sm" onClick={load} data-testid="revenue-refresh">
                        <RefreshCw size={12} className="mr-1" /> Refresh
                    </Button>
                    <Button variant="ghost" size="sm" onClick={onLock} data-testid="revenue-relock">
                        <Lock size={12} className="mr-1" /> Lock
                    </Button>
                </div>
            </div>

            {loading && !stats ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <Stat label="Revenue this period" value={money(stats?.total_revenue_dollars)} tone="emerald-600" Icon={DollarSign} testId="rev-stat-total" />
                        <Stat label="Paid events" value={stats?.total_paid_events ?? 0} Icon={TrendingUp} testId="rev-stat-events" />
                        <Stat label="Active subs" value={stats?.active_subscriptions ?? 0} Icon={Users} testId="rev-stat-active" />
                        <Stat label="Past due" value={stats?.past_due_subscriptions ?? 0} tone="destructive" Icon={AlertTriangle} testId="rev-stat-past-due" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-2 card-soft p-4" data-testid="revenue-feed">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-heading font-medium text-lg">Live payment feed</h3>
                                <div className="text-xs text-muted-foreground">{feed.length} recent events</div>
                            </div>
                            {feed.length === 0 ? (
                                <div className="py-12 text-center text-sm text-muted-foreground">
                                    No paid events yet. When your first customer pays, they&apos;ll appear here.
                                </div>
                            ) : (
                                <div className="divide-y divide-border">
                                    {feed.map((ev, i) => (
                                        <div key={ev.stripe_event_id || i} className="flex items-center justify-between py-3 gap-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <Circle
                                                    size={8}
                                                    className={`shrink-0 fill-current ${ev.livemode ? "text-emerald-500" : "text-amber-500"}`}
                                                />
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium truncate">
                                                        {ev.user_name || ev.user_email || "Unknown customer"}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground truncate">
                                                        {ev.user_email && ev.user_name ? ev.user_email + " · " : ""}
                                                        {ev.plan_label} · <span className="font-mono">{ev.kind}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-heading font-semibold mono-score">
                                                    {ev.amount_dollars > 0 ? money(ev.amount_dollars) : "—"}
                                                </div>
                                                <div className="text-[11px] text-muted-foreground">{relTime(ev.created_at)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="card-soft p-4" data-testid="revenue-by-plan">
                            <h3 className="font-heading font-medium text-lg mb-3">By plan · {range}</h3>
                            {topPlan && (
                                <div className="mb-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-3">
                                    <div className="text-[11px] text-emerald-600 font-semibold uppercase tracking-wider">Top earner</div>
                                    <div className="font-heading font-medium mt-1">{topPlan.label}</div>
                                    <div className="text-emerald-600 mono-score font-bold text-xl">{money(topPlan.total_dollars)}</div>
                                    <div className="text-xs text-muted-foreground">{topPlan.count} events</div>
                                </div>
                            )}
                            {(stats?.by_plan || []).length === 0 && (
                                <div className="py-8 text-center text-sm text-muted-foreground">No payments in this window.</div>
                            )}
                            <div className="space-y-2">
                                {(stats?.by_plan || []).slice(1).map((p) => (
                                    <div key={p.plan_key} className="flex items-center justify-between text-sm">
                                        <div className="truncate">{p.label}</div>
                                        <div className="text-right shrink-0 ml-2">
                                            <div className="font-mono">{money(p.total_dollars)}</div>
                                            <div className="text-[11px] text-muted-foreground">{p.count} events</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default function AdminRevenue() {
    const [token, setToken] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
    const unlock = (t) => setToken(t);
    const lock = () => { sessionStorage.removeItem(SESSION_KEY); setToken(""); };
    if (!token) return <TwoFactorGate onUnlock={unlock} />;
    return <RevenueDashboard token={token} onLock={lock} />;
}
